/**
 * ── SEND-TIME PRICE + SOLD FOR THE LISTING TABLE (Jack, 2026-08-21) ────────────
 *
 * "yes to updating send time and handles sold … do not want now/was pricing."
 *
 * The ECH Listing Table bakes its values at BUILD time, so an email built Monday
 * and sent Friday ships Monday's prices and lists homes that have since sold.
 * The production Stensul module (harvest asset 1023082, filed in the vault
 * 2026-08-21) already solves this: it resolves each row from the
 * `ent.Sitecore Homesites` DE by `lot_id` at SEND time. This transform grafts
 * exactly that — and ONLY that — onto our export:
 *
 *   · each filled row gets a LookupRows("ent.Sitecore Homesites","lot_id",…)
 *     prelude (field names `price` / `homesite_status`, verified against the
 *     production module's own source, never guessed);
 *   · the price cell renders the LOOKED-UP price, falling back to the baked one
 *     when the lot is missing from the DE or carries no price;
 *   · a SOLD status swaps the price and View cells for the red bold SOLD mark —
 *     the Stensul treatment, on Layne's row;
 *   · NO was/now pricing, per Jack. `price_previous` is not read.
 *
 * FORMAT STABILITY IS THE OTHER HALF OF THE ASK ("make sure the format of the
 * block sticks when adding a homesite"). Both row variants are LAYNE'S OWN
 * RENDERED <tr>, string-surgered in two cells — nothing is re-laid-out, no
 * second row template exists to drift, and a document this transform declines
 * (guard failure, no lots, hand-edited row) ships the baked row byte-identical.
 *
 * EXPORT ONLY. The canvas, the form's preview rail, and saved favorites keep the
 * baked snapshot — raw `%%[ ]%%` between <tr>s would render as stray text in any
 * browser preview (HTML parsers foster-parent text out of tables). The submit
 * path (FormView) is the one place the email leaves for SFMC, so it is the one
 * caller.
 *
 * NO `>` INSIDE AMPSCRIPT, DELIBERATELY. Comparisons use `!=` throughout —
 * SFMC's publish pipeline has escaped a bare `>` before (the CloudPage trap,
 * cloudpage-fixer), and `RowCount(@r) != 0` needs nothing it can break.
 */
import type { AnyBlockInstance } from "../email/document";
import { echMeta, isEch } from "./echRegistry";

/** the marker + its own row: `<!-- HOME n -->` labels the ONE `<tr>` after it */
const ROW_RE = /<!--\s*HOME\s+(\d+)\s*-->\s*(<tr[^>]*>[\s\S]*?<\/tr>)/gi;

/** EVERY home-row block, in document order — the html's HOME sequences map onto
 *  this list positionally, so it must not be filtered down to the lot-carrying
 *  ones (a CSV-filled table between two fed ones would shift the mapping) */
function listingBlocks(blocks: AnyBlockInstance[]): AnyBlockInstance[] {
  return (blocks || []).filter((b) => {
    if (!b || !isEch(b.type)) return false;
    const meta = echMeta(b.type);
    return !!(meta && meta.declared && meta.declared.home1Homesite);
  });
}

const soldSpan = '<span style="white-space:nowrap;font-weight:bold;color:#AA1F23;">SOLD</span>';

/** everything between the td's opening tag and its `</td>` swapped for `inner` */
function swapCell(cell: string, inner: string): string {
  const open = cell.indexOf(">");
  return open < 0 ? cell : cell.slice(0, open + 1) + inner;
}

/**
 * One row's two variants off the ORIGINAL rendered `<tr>`.
 * Cells split on `</td>`: [0] homesite/plan · [1] specs · [2] price · [3] view.
 * Returns null when the row does not look like Layne's four-cell shape — the
 * caller then leaves the baked row untouched rather than guessing.
 */
function rowVariants(tr: string, bakedPrice: string, priceVar: string): { live: string; sold: string } | null {
  const parts = tr.split("</td>");
  /* 4 cells → 5 parts (the tail holds `</tr>`); a nested row would also break
     the split count, so this guard covers both */
  if (parts.length !== 5 || /<tr[\s>]/i.test(parts[0].replace(/^<tr[^>]*>/i, ""))) return null;
  const expr = "%%=v(" + priceVar + ")=%%";
  /* live: the baked price string swaps for the send-time value; a row with no
     baked price keeps its empty cell (nothing to anchor the swap on) */
  const live = bakedPrice && parts[2].indexOf(bakedPrice) !== -1
    ? parts[2].replace(bakedPrice, expr)
    : parts[2];
  const liveRow = [parts[0], parts[1], live, parts[3], parts[4]].join("</td>");
  const soldRow = [parts[0], parts[1], swapCell(parts[2], soldSpan), swapCell(parts[3], soldSpan), parts[4]].join("</td>");
  return { live: liveRow, sold: soldRow };
}

/** AMPscript-string-safe: the lot id and baked price ride inside "quotes" */
const q = (s: string): string => String(s || "").replace(/["\\]/g, "");

/**
 * The export transform. Baked HTML in, send-time HTML out; any row (or document)
 * it cannot handle safely comes back byte-identical.
 */
export function sendTimeListing(html: string, blocks: AnyBlockInstance[]): string {
  const lst = listingBlocks(blocks);
  if (!lst.length || !html || html.indexOf("<!-- HOME") < 0) return html;
  if (!lst.some((b) => String((b.data || {}).__lots || "").trim())) return html;
  /* rows are matched document-wide in order; within one block the surviving row
     numbers only ASCEND (empties are dropped, order is fixed), so a number that
     fails to ascend means the next listing block's rows have begun */
  let bi = -1;
  let prevN = Infinity;
  return html.replace(ROW_RE, (m0, nStr: string, tr: string) => {
    const n = +nStr;
    if (n <= prevN) bi += 1;
    prevN = n;
    const b = lst[bi];
    if (!b) return m0;
    const d = (b.data || {}) as Record<string, unknown>;
    let lots: Record<string, string> = {};
    try { lots = JSON.parse(String(d.__lots || "{}")); } catch { /* baked row stands */ }
    const lot = String(lots[String(n)] || "");
    if (!lot) return m0;
    /* a row the requester took over is THEIRS — the displayed home may no longer
       match the lot the fill recorded, so send-time resolution would price the
       wrong home. Baked row stands. */
    const off = String(d.__socOff || "");
    if (off && new RegExp("\\bhome" + n + "[A-Za-z]+\\b").test(off)) return m0;
    const bakedPrice = String(d["home" + n + "Price"] || "").trim();
    const V = "@echst" + (bi + 1) + "_" + n; /* per block+row, collision-free */
    const vars = rowVariants(tr, bakedPrice, V + "p");
    if (!vars) return m0;
    const pre =
      "%%[ set " + V + "r = LookupRows(\"ent.Sitecore Homesites\",\"lot_id\",\"" + q(lot) + "\")" +
      " set " + V + "s = 0 set " + V + "p = \"" + q(bakedPrice) + "\"" +
      " if RowCount(" + V + "r) != 0 then" +
      " set " + V + "w = Row(" + V + "r,1)" +
      " if IndexOf(Uppercase(Trim(Field(" + V + "w,\"homesite_status\"))),\"SOLD\") != 0 then set " + V + "s = 1 endif" +
      " set " + V + "lp = Trim(Field(" + V + "w,\"price\"))" +
      " if not Empty(" + V + "lp) then set " + V + "p = FormatCurrency(" + V + "lp,\"en-US\",0) endif" +
      " endif ]%%";
    return "<!-- HOME " + n + " -->" + pre +
      "%%[ if " + V + "s == 1 then ]%%" + vars.sold +
      "%%[ else ]%%" + vars.live + "%%[ endif ]%%";
  });
}
